/**
 * src/gm/session/session-codec.ts — 会话显式化：SessionRecord 编解码（设计稿 §2.1）。
 *
 * 核心纪律（sessionCodec 借鉴）：record 带 version 字段，decode 按版本迁移——
 * 会话是持久物证，结构演进时旧档必须可读，不许"格式变了就丢档"。
 *
 * 安全：messages 只含 prompt 与模型输出，Key 永不入内（Key 只在 api-client 请求头）。
 */

import type { ChatMessage, Usage } from '../employees/api-client'
import type { ModelChoice } from '../platform/model-map'

export type StageKind = 'plan' | 'art' | 'code' | 'review'
export type SessionState = 'active' | 'exhausted' | 'closed'

export const SESSION_VERSION = 1

export interface SessionRecord {
  id: string // ses_<projectId>_<stage>
  version: number // codec 版本号
  projectId: string
  stage: StageKind
  employeeId: string // 评审阶段为 'reviewer'
  modelChoice: ModelChoice
  messages: ChatMessage[] // 完整对话历史（含各轮重试的 assistant/user 追加）
  usage: Usage // 累计精确用量（含重试与续写）
  rounds: number // 已用校验反馈轮次（上限 MAX_VALIDATE_ROUNDS）
  continuationUsed: boolean
  partialRaw?: string // 程序岗流式已收部分（节流快照，崩溃续跑用）
  state: SessionState
  createdAt: string
  updatedAt: string
}

const ZERO_USAGE: Usage = { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 }

export function createSession(init: {
  projectId: string
  stage: StageKind
  employeeId: string
  modelChoice: ModelChoice
  messages: ChatMessage[]
}): SessionRecord {
  const now = new Date().toISOString()
  return {
    id: `ses_${init.projectId}_${init.stage}`,
    version: SESSION_VERSION,
    projectId: init.projectId,
    stage: init.stage,
    employeeId: init.employeeId,
    modelChoice: init.modelChoice,
    messages: init.messages,
    usage: { ...ZERO_USAGE },
    rounds: 0,
    continuationUsed: false,
    state: 'active',
    createdAt: now,
    updatedAt: now,
  }
}

export function encodeSession(rec: SessionRecord): string {
  return JSON.stringify(rec)
}

/** 按版本迁移解码；无法识别返回 null（调用方按"无会话"重建，不崩溃） */
export function decodeSession(raw: string | null): SessionRecord | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  const rec = parsed as Partial<SessionRecord> | null
  if (!rec || typeof rec !== 'object') return null
  switch (rec.version) {
    case SESSION_VERSION:
      return rec as SessionRecord
    default:
      // 未来版本迁移入口：v1 → v2 在这里补字段。当前只有 v1。
      return null
  }
}
